import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

type PageChangeEvent = { first: number; rows: number };

@Component({
  selector: 'app-pagination-mobile-version',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './pagination-mobile-version.component.html',
  styleUrls: ['./pagination-mobile-version.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationMobileVersionComponent implements OnChanges {
  @Input() totalRecords = 0;
  @Input() skip = 0;
  @Input() take = 10;
  @Input() rowsPerPageOptions: number[] = [10, 20, 50, 100];

  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  rowsControl = new FormControl<number>(10, { nonNullable: true });

  constructor(private cdr: ChangeDetectorRef) {
    this.rowsControl.valueChanges.subscribe((rows) => {
      if (!rows || rows <= 0) {
        return;
      }
      this.take = rows;
      this.emit(0);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['take'] && this.rowsControl.value !== this.take) {
      this.rowsControl.setValue(this.take, { emitEvent: false });
    }
    if (changes['skip'] || changes['take'] || changes['totalRecords']) {
      this.cdr.markForCheck();
    }
  }

  goToFirstPage(): void {
    this.emit(0);
  }

  goToPreviousPage(): void {
    this.emit(Math.max(0, this.skip - this.take));
  }

  goToNextPage(): void {
    this.emit(this.skip + this.take);
  }

  goToLastPage(): void {
    const totalPages = Math.max(1, Math.ceil(this.totalRecords / this.take));
    this.emit(Math.max(0, (totalPages - 1) * this.take));
  }

  goToPageNumber(page: number): void {
    this.emit(Math.max(0, (page - 1) * this.take));
  }

  getPaginationEndIndex(): number {
    return Math.min(this.skip + this.take, this.totalRecords);
  }

  isCurrentPage(page: number): boolean {
    const take = Number(this.take) || 1;
    const skip = Number(this.skip) || 0;
    const currentPage = Math.floor(skip / take) + 1;
    return page === currentPage;
  }

  trackByPage(_index: number, page: number): number {
    return page;
  }

  getVisiblePages(): number[] {
    const take = Number(this.take) || 1;
    const skip = Number(this.skip) || 0;
    const currentPage = Math.floor(skip / take) + 1;
    const totalPages = Math.max(1, Math.ceil(this.totalRecords / take));
    const maxVisible = 5;

    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    const pages: number[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  private emit(first: number): void {
    this.pageChange.emit({ first, rows: this.take });
  }
}
